import { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey, faCheck, faEye, faEyeSlash, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import config from "../../config";

function SecuritySection({ darkMode, t }) {
  const [form, setForm] = useState({ current: "", newPwd: "", confirm: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  const update = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));

  // validation state - computed from form values
  const validation = useMemo(() => {
    const errors = [];
    const allFilled = form.current.length > 0 && form.newPwd.length > 0 && form.confirm.length > 0;
    
    if (form.newPwd && form.newPwd.length < 8) {
      errors.push("min8");
    }
    if (form.newPwd && form.confirm && form.newPwd !== form.confirm) {
      errors.push("mismatch");
    }
    if (form.newPwd && form.current && form.newPwd === form.current) {
      errors.push("same");
    }

    // button enabled only when all fields filled and no errors
    const canSubmit = allFilled && errors.length === 0;

    return { errors, canSubmit, allFilled };
  }, [form]);

  const submit = async () => {
    setAlert(null);

    if (!validation.canSubmit) {
      if (!validation.allFilled) {
        setAlert({ type: "error", msg: t("Please fill in all fields") });
      } else if (validation.errors.includes("mismatch")) {
        setAlert({ type: "error", msg: t("Passwords do not match") });
      } else if (validation.errors.includes("min8")) {
        setAlert({ type: "error", msg: t("Password must be at least 8 characters") });
      } else if (validation.errors.includes("same")) {
        setAlert({ type: "error", msg: t("New password must be different from current") });
      }
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${config.apiUrl}/user/password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: JSON.stringify({ current_password: form.current, new_password: form.newPwd }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setAlert({ type: "success", msg: t("Password updated") });
        setForm({ current: "", newPwd: "", confirm: "" });
      } else if (res.status === 401 || res.status === 403) {
        setAlert({ type: "error", msg: t("Current password is incorrect") });
      } else if (res.status === 429) {
        setAlert({ type: "error", msg: t("Too many attempts. Please try again later.") });
      } else {
        setAlert({ type: "error", msg: data.message || t("Failed to update password") });
      }
    } catch {
      setAlert({ type: "error", msg: t("Failed to update password") });
    }
    setSaving(false);
    setTimeout(() => setAlert(null), 5000);
  };

  return (
    <div className="profile-section">
      <div className="profile-section-header">
        <div className="profile-section-icon">
          <FontAwesomeIcon icon={faKey} />
        </div>
        <div>
          <h2 className="profile-section-title">{t("Change Password")}</h2>
          <p className="profile-section-desc">{t("Keep your account secure")}</p>
        </div>
      </div>

      {alert && (
        <div className={`profile-alert ${alert.type}`}>
          <FontAwesomeIcon icon={alert.type === "success" ? faCheck : faExclamationTriangle} />
          {alert.msg}
        </div>
      )}

      <div className="profile-form-grid">
        <div className="profile-form-group full">
          <label className="profile-label">{t("Current Password")}</label>
          <div className="profile-input-wrap">
            <input
              type={showCurrent ? "text" : "password"}
              className="profile-input"
              placeholder={t("Enter current password")}
              value={form.current}
              onChange={(e) => update("current", e.target.value)}
            />
            <span className="profile-input-icon" onClick={() => setShowCurrent(!showCurrent)}>
              <FontAwesomeIcon icon={showCurrent ? faEyeSlash : faEye} />
            </span>
          </div>
        </div>

        <div className="profile-form-group">
          <label className="profile-label">{t("New Password")}</label>
          <div className="profile-input-wrap">
            <input
              type={showNew ? "text" : "password"}
              className="profile-input"
              placeholder={t("Enter new password")}
              value={form.newPwd}
              onChange={(e) => update("newPwd", e.target.value)}
            />
            <span className="profile-input-icon" onClick={() => setShowNew(!showNew)}>
              <FontAwesomeIcon icon={showNew ? faEyeSlash : faEye} />
            </span>
          </div>
        </div>

        <div className="profile-form-group">
          <label className="profile-label">{t("Confirm Password")}</label>
          <div className="profile-input-wrap">
            <input
              type={showConfirm ? "text" : "password"}
              className="profile-input"
              placeholder={t("Confirm new password")}
              value={form.confirm}
              onChange={(e) => update("confirm", e.target.value)}
            />
            <span className="profile-input-icon" onClick={() => setShowConfirm(!showConfirm)}>
              <FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} />
            </span>
          </div>
        </div>
      </div>

      <div className="profile-btn-row">
        <button className="profile-btn profile-btn-primary" onClick={submit} disabled={!validation.canSubmit || saving}>
          <FontAwesomeIcon icon={faCheck} />
          {saving ? t("Updating...") : t("Update Password")}
        </button>
      </div>
    </div>
  );
}

export default SecuritySection;
